import {Component, computed, inject, signal} from '@angular/core';
import {MaterialModule} from '../../../core/modules/material.module';
import {SharedModule} from '../../../core/modules/shared.module';
import {EventsService} from '../../../core/services/events.service';
import {NgxSpinnerService} from 'ngx-spinner';
import {FuseConfigService} from '../../../core/services/config.service';
import {TargetGroupService} from '../../../core/services/targetGroup.service';
import {EventTypeService} from '../../../core/services/eventType.service';
import {PlatformsService} from '../../../core/services/platforms.service';
import {UserService} from '../../../core/services/users.service';
import {toObservable} from '@angular/core/rxjs-interop';
import {debounceTime, Subject} from 'rxjs';
import {PagesContainerComponent} from '../pages-container/pages-container.component';
import {FilterComponent} from '../../../core/components/filter/filter.component';
import {
  CalendarDatePipe,
  CalendarDayViewComponent,
  CalendarMonthViewComponent,
  CalendarNextViewDirective,
  CalendarPreviousViewDirective,
  CalendarTodayDirective,
  CalendarWeekViewComponent, DateAdapter, provideCalendar,
  CalendarView
} from 'angular-calendar';
import {addDays, parseISO, startOfDay, startOfISOWeek} from 'date-fns';
import {TranslateService} from '@ngx-translate/core';
import {adapterFactory} from 'angular-calendar/date-adapters/date-fns';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';

@Component({ selector: 'app-calendar',
  templateUrl: './calendar-ref.component.html',
  styleUrls: ['./calendar-ref.component.scss'],
  standalone: true,
  imports: [MaterialModule, SharedModule, PagesContainerComponent, FilterComponent,
    CalendarMonthViewComponent,
    CalendarWeekViewComponent,
    CalendarDayViewComponent,
    CalendarNextViewDirective,
    CalendarPreviousViewDirective,
    CalendarTodayDirective,
    CalendarDatePipe],
  providers: [EventsService, TargetGroupService, EventTypeService,
    provideCalendar({
      provide: DateAdapter,
      useFactory: adapterFactory,
    })]
})
export class CalendarRefComponent {
  private eventService = inject(EventsService);
  private spinner = inject(NgxSpinnerService);
  private fuseConfig = inject(FuseConfigService);
  private targetService = inject(TargetGroupService);
  private eventTypeService = inject(EventTypeService);
  private platformsService = inject(PlatformsService);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  readonly CalendarView = CalendarView;
  view = signal<any>('week');
  viewDate = new Date();
  locale = signal<string>(this.translate.currentLang || 'de');

  events = signal([]);
  platformId = this.fuseConfig.selectedPlatformId_Sig;
  pagination = signal<any>(null);
  role = this.fuseConfig.role_Sig;
  userId = computed(() => {
    let id = this.fuseConfig.userId_Sig();
    id = +id * 12345654321;
    return id.toString(16);
  });
  filterData = signal<any>({
    publicEvents: true,
    eventRegistration: true,
    mileStoneEvent: true,
    migrate: true,
    myEvents: true
  });
  userFiltersFromDB = signal<any>({});
  userFilters = signal<any>({});
  userFilterId = signal(null);

  constructor() {

    const trigger = computed(() => {
      return {
        platform: this.platformId(),
        countryId: this.eventService.countryId(),
        citiesId: this.eventService.citiesId(),
        organizationsId: this.eventService.organizationsId(),
        eventTypes: this.eventService.eventTypes(),
        publicEvents: this.eventService.publicEvents(),
        eventWithRegistration: this.eventService.eventRegistration(),
        targetGroups: this.eventService.targetGroups(),
        mileStoneEvent: this.eventService.milestone(),
        myEvents: this.eventService.myEvents()
      };
    });

    toObservable(trigger).pipe(debounceTime(300)).subscribe(() => {
      this.getEvents();
    });

    this.eventService.setToAll();
  }


  ngOnInit() {
    this.getCountries();
    this.getTargetGroups();
    this.getEventTypes();
    this.getUserFilters();
  }

  setView(view: CalendarView) {
    this.view.set(view);
  }

  closeOpenMonthViewDay() {

  }

  getUserFilters() {
    this.userService.getFilter('event').subscribe((res: any) => {
      if (res) {
        this.userFiltersFromDB.set(JSON.parse(res.config));
        this.userFilters.set(JSON.parse(res.config));
        this.userFilterId.set(res.id);
      }
    });
  }

  getCountries() {
    this.eventService.getCountry().subscribe((res: any) => {
      if (res.length === 1) {
        this.getCantons(res[0].id);
      } else {
        this.filterData.update(f => ({...f, countries: res}));
      }
    });
  }

  getCantons(id) {
    this.eventService.getCity(id).subscribe((res: any) => {
      if (res.length === 1) {
        this.eventService.setCities({selected: true, canton: res[0].id});
        this.getOrganizations();
      } else {
        this.filterData.update(f => ({...f, cantons: res}));
      }
    });
  }

  getOrganizations() {
    this.eventService.getOrganizationByCantons().subscribe((res: any) => {
      if (res.length === 1) {
        this.eventService.setOrganizations({selected: true, organization: res[0].id});
      } else {
        this.filterData.update(f => ({...f, organizations: res}));
      }
    });
  }

  getPlatforms() {
    this.platformsService.getPlatforms().subscribe((res: any) => {
      this.filterData.update(f => ({...f, platforms: res}));
    });
  }

  getTargetGroups() {
    this.targetService.getTargetGroups(false).then((data: any) => {
      this.filterData.update(f => ({...f, targetGroups: data.targetGroups}));
    });
  }

  getEventTypes() {
    this.eventTypeService.getEventTypeDropDown().subscribe((data: any) => {
      this.filterData.update(f => ({...f, eventTypes: data}));
    });
  }

  chooseFilters(e) {
    if (e.choose.hasOwnProperty('country')) {
      this.eventService.setCountryId(e.choose.country);
      this.getCantons(e.choose.country);
    } else if (e.choose.hasOwnProperty('canton')) {
      this.eventService.setCities(e.choose);
      this.getOrganizations();
    } else if (e.choose.hasOwnProperty('organization')) {
      this.eventService.setOrganizations(e.choose);
    } else if (e.choose.hasOwnProperty('targetGroups')) {
      this.eventService.setTargetGroups(e.choose);
    } else if (e.choose.hasOwnProperty('eventType')) {
      this.eventService.setEventTypes(e.choose);
    } else if (e.choose.hasOwnProperty('publicEvents')) {
      this.eventService.setPublic(e.choose.publicEvents);
    } else if (e.choose.hasOwnProperty('eventRegistration')) {
      this.eventService.setRegistration(e.choose.selected);
    } else if (e.choose.hasOwnProperty('mileStoneEvent')) {
      this.eventService.setMilestone(e.choose.selected);
    } else if (e.choose.hasOwnProperty('myEvents')) {
      this.eventService.setMyEvents(e.choose.selected);
    } else if (e.choose.hasOwnProperty('platforms')) {
      this.platformId.set(e.choose.platforms);
    } else if (e.choose === 'all') {
      this.eventService.setToAll();
      this.filterData.update(f => {
        const {organizations, cantons, ...rest} = f;
        return rest;
      });
    }
    if (e.choose === 'all') {
      this.fuseConfig.activeFilter_Sig.set(false);
    } else {
      this.fuseConfig.activeFilter_Sig.set(true);
    }
    this.setDataFromFilter(e);
  }

  getEvents() {
    this.spinner.show();
    this.eventService.eventsForCalendarV2(1, 10000, '', '',
      '', false, false, false, this.platformId() || '').subscribe({
      next: (res: any) => {
        res = res.map(event => ({
          ...event,
          start: parseISO(event.start),
          end: parseISO(event.end)
        }));
        console.log(res);
        this.events.set(res);
        this.spinner.hide();
      },
      error: (err) => {
        this.spinner.hide();
      }
    });
  }

  setDataFromFilter(e) {
    if (e.choose === 'all') {
      this.userFilters.set({});
      this.userFiltersFromDB.set({});
    } else if (e.choose.hasOwnProperty('country')) {
      this.userFilters.update(filters => {
        const {organizations, cantons, ...rest} = filters;
        return {...rest, country: e.choose.country};
      });
    } else if (e.choose.hasOwnProperty('canton')) {
      const _cities = this.eventService.getCities();
      this.userFilters.update(filters => ({...filters, cantons: _cities}));
    } else if (e.choose.hasOwnProperty('organization')) {
      const _organizations = this.eventService.getOrganizations();
      this.userFilters.update(filters => ({...filters, organizations: _organizations}));
    } else if (e.choose.hasOwnProperty('targetGroups')) {
      const _targetGroups = this.eventService.getTargetGroups();
      this.userFilters.update(filters => ({...filters, targetGroups: _targetGroups}));
    } else if (e.choose.hasOwnProperty('eventType')) {
      const _eventType = this.eventService.getEventTypes();
      this.userFilters.update(filters => ({...filters, eventTypes: _eventType}));
    } else if (e.choose.hasOwnProperty('eventRegistration')) {
      this.userFilters.update(filters => ({...filters, eventRegistration: e.choose.selected}));
    } else if (e.choose.hasOwnProperty('publicEvents')) {
      this.userFilters.update(filters => ({...filters, publicEvents: e.choose.publicEvents}));
    } else if (e.choose.hasOwnProperty('platforms')) {
      this.userFilters.update(filters => ({...filters, platforms: e.choose.platforms}));
    } else if (e.choose.hasOwnProperty('mileStoneEvent')) {
      this.userFilters.update(filters => ({...filters, mileStoneEvent: e.choose.selected}));
    } else if (e.choose.hasOwnProperty('myEvents')) {
      this.userFilters.update(filters => ({...filters, myEvents: e.choose.selected}));
    }
    if ((this as any).filterTimeout) {
      clearTimeout((this as any).filterTimeout);
    }

    (this as any).filterTimeout = setTimeout(() => {
      this.setUserFilters();
    }, 1000);
  }

  setUserFilters() {
    this.userService.setFilters('event', this.userFilters(), this.userFilterId() || '')
      .subscribe((res: any) => {
      });
  }

  editEvent(event) {
    this.router.navigate(['/pages/event/detail'], {queryParams: {id: event.meta.originalEvent.id}});
  }

  dayHeaderClicked(event) {
    this.viewDate = event.day.date;
    this.view.set(CalendarView.Day);
  }

  copyCalendarUrl() {
    const urlElement = document.getElementById('calendarUrl') as HTMLInputElement;
    const urlToCopy = urlElement?.value || '';

    if (urlToCopy) {
      navigator.clipboard.writeText(urlToCopy).then(() => {
        this.toastr.success('Kalender-Abo-Link wurde kopiert!', 'Success!');
      }).catch(err => {
        console.error('copy error: ', err);
      });
    }
  }
}
